// src/blogs/PostBody.jsx
// Shared post-content renderer for all three blogs, so text, links and images
// work the same everywhere. A post's content is either:
//   - `body`: an array of blocks, rendered in order —
//       "some text"                          a paragraph (line breaks kept)
//       { image, alt?, caption?, width?, height? }
//                                            a figure
//   - or, when there's no `body`, just `excerpt` as a single paragraph — so
//     every existing text-only post keeps working untouched.
// See the shape note atop blogData.js for an example.
//
// Inside any text, [label](target) makes a link:
//   [label](https://example.com)   external — opens in a new tab
//   [label](mailto:me@example.com) an email link
//   [label](post:movie-001)        another post, by id — opens the right blog
//                                  and scrolls to it (see postLinks.js)
// Anything else (javascript:, unknown post ids, ...) is shown as plain text
// rather than becoming a link, so content can't smuggle in odd URL schemes.
//
// Each blog passes its own `textClassName`, so paragraphs keep that blog's
// typography; figures and links are styled once, in BlogShell.css.
import React from "react";

import { useBlogNavigation } from "./BlogNavContext.js";
import { handlePostLinkClick, postHref } from "./postLinks.js";

const BASE = import.meta.env.BASE_URL;

// Image sources can be written the same ways as elsewhere in the app:
//   "blog/posts/rain.jpg" or "/blog/posts/rain.jpg"  -> a file under public/,
//                                     prefixed with the Vite base (/resume/)
//   "https://..." / "data:..."       -> used as-is
//   an imported asset (import rain from "./rain.jpg") -> Vite already put the
//                                     base on it, so it's left alone
function resolveAssetUrl(src) {
  if (/^(https?:|data:|blob:|\/\/)/i.test(src)) return src;
  if (BASE !== "/" && src.startsWith(BASE)) return src;
  return `${BASE}${src.replace(/^\/+/, "")}`;
}

function PostFigure({ block }) {
  const { image, alt, caption, width, height } = block;
  return (
    <figure className="post-figure">
      <img
        src={resolveAssetUrl(image)}
        // Empty alt marks an image decorative, so fall back to the caption
        // rather than leaving screen readers with nothing.
        alt={alt ?? caption ?? ""}
        // width/height (any consistent pair, e.g. the file's pixel size) let
        // the browser reserve the right amount of space before the image loads.
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
      />
      {caption && <figcaption className="post-figure__caption">{caption}</figcaption>}
    </figure>
  );
}

function PostLink({ label, target }) {
  const { veilNavigate } = useBlogNavigation();

  if (target.startsWith("post:")) {
    const id = target.slice("post:".length);
    const href = postHref(id);
    if (!href) {
      if (import.meta.env.DEV) console.warn(`[blog] link to unknown post "${id}"`);
      return label;
    }
    return (
      <a
        className="post-link"
        href={href}
        onClick={(event) => handlePostLinkClick(event, id, veilNavigate)}
      >
        {label}
      </a>
    );
  }

  if (/^https?:\/\//i.test(target)) {
    return (
      <a
        className="post-link post-link--external"
        href={target}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  if (/^mailto:/i.test(target)) {
    return (
      <a className="post-link" href={target}>
        {label}
      </a>
    );
  }

  return label;
}

// [label](target) — no nested links, no whitespace inside the target. The
// target may hold one level of balanced parentheses, so URLs like
// https://en.wikipedia.org/wiki/Foo_(bar) survive intact.
const LINK_PATTERN = /\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g;

function renderInline(text) {
  const nodes = [];
  let last = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    nodes.push(<PostLink key={match.index} label={match[1]} target={match[2]} />);
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export default function PostBody({ post, textClassName }) {
  const blocks = Array.isArray(post.body) ? post.body : post.excerpt ? [post.excerpt] : [];

  return (
    <div className="post-body">
      {blocks.map((block, i) => {
        if (typeof block === "string") {
          return (
            <p key={i} className={textClassName}>
              {renderInline(block)}
            </p>
          );
        }
        if (block?.image) return <PostFigure key={i} block={block} />;
        return null;
      })}
    </div>
  );
}
